import React, { useState, useEffect } from 'react'
import { Heart } from 'react-feather'
import clsx from 'clsx'
import { useShowMessage } from '../../providers'
import { markFavourite } from '@pepper/utils/src/api/users/users'
import { UnfavouriteCreatorModal } from '../UnfavouriteCreatorModal/UnfavouriteCreatorModal'
import s from './MarkFavourite.module.css'

type Props = {
  creator: {
    id: string
    name: string
    role: string
    isFavourite: boolean
  }
  assignmentId?: string
  small?: boolean
  onSucess: ((value: boolean) => void) | undefined
  isDisabled: boolean
  isTextHidden?: boolean
  companyId: string
}

export const MarkFavourite = ({
  creator,
  assignmentId,
  small,
  onSucess,
  isDisabled,
  isTextHidden = false,
  companyId
}: Props): JSX.Element => {
  const [isEnabled, setIsEnabled] = useState(creator.isFavourite)
  const [showModal, setShowModal] = useState(false)
  const showMessage = useShowMessage()

  useEffect(() => {
    setIsEnabled(creator.isFavourite)
  }, [creator.isFavourite])

  const markAsFavourite = async () => {
    try {
      const response = await markFavourite({
        companyId,
        creatorId: creator.id,
        assignmentId
      })
      return response
    } catch (error) {
      return error
    }
  }

  const handleClick = async () => {
    if (isEnabled) {
      setShowModal(true)
      return
    }

    setIsEnabled(true)
    const { status, error } = await markAsFavourite()

    if (status === 'ok') {
      showMessage({
        type: 'success',
        message: `${creator.name} has been added to favourites`
      })
      onSucess?.(true)
    } else if (error) {
      setIsEnabled(false)
      showMessage({
        type: 'error',
        message: `Unable to mark as favourites. Please try again`
      })
    }
  }

  const helperText = isEnabled ? `Favourite Creator` : 'Mark as favourite'
  const title = isTextHidden ? helperText : ''

  return (
    <>
      <button
        className={clsx(s.button, { [s.disabled]: isDisabled })}
        onClick={handleClick}
        data-id={isEnabled ? 'mark_favouriteBtn' : 'mark_unFavouriteBtn'}
        disabled={isDisabled}
        title={title}
      >
        <Heart
          className={isEnabled ? s.bouce : ''}
          size={small ? 16 : 20}
          fill={isEnabled ? 'var(--color-blue-500)' : 'none'}
        />
        {!isTextHidden ? helperText : ''}
      </button>
      {showModal && (
        <UnfavouriteCreatorModal
          creator={creator}
          companyId={companyId}
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSucess={() => {
            onSucess?.(false)
            setIsEnabled(false)
          }}
        />
      )}
    </>
  )
}
